package org.example.project.enginefinanzen

import androidx.compose.runtime.*
import kotlinx.browser.document
import kotlinx.coroutines.launch
import org.example.project.enginetabular.TabularScreen
import org.example.project.enginetabular.TabularSplitScreen
import org.example.project.enginetabular.TabularToolbarButton
import org.example.project.enginetabular.TABULAR_DOCUMENT_ICON_VALUE
import org.example.project.screens_sys.EingangsbelegeApi
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLInputElement

private const val EINGANGS_MWST = 0.19
private val belegHeaders = listOf("Belegnummer", "Belegdatum", "Händler", "Brutto", "MwSt.", "Netto", "PDF")
private val positionHeaders = listOf("Pos.", "Belegnummer", "Artikelbezeichnung", "Menge", "Brutto", "MwSt.", "Netto")

private fun parseEuro(value: String): Double {
    var raw = value.trim().replace("€", "").replace(" ", "")
    if (raw.contains(',')) raw = raw.replace(".", "").replace(',', '.')
    return raw.toDoubleOrNull()?.coerceAtLeast(0.0) ?: 0.0
}

private fun euro(value: Double): String =
    (js("new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format")(value) as String)

private fun calculatedMoney(bruttoText: String): Pair<String, String> {
    val brutto = parseEuro(bruttoText)
    val netto = brutto / (1.0 + EINGANGS_MWST)
    return euro(brutto - netto) to euro(netto)
}

private fun positionFromRow(rowId: String, cols: List<String>, beleg: All) = All(
    id = rowId,
    pos = cols.getOrNull(0).orEmpty(),
    belegId = beleg.id,
    belegnummer = beleg.belegnummer,
    artikelbezeichnung = cols.getOrNull(2).orEmpty(),
    menge = cols.getOrNull(3).orEmpty().ifBlank { "1" },
    brutto = cols.getOrNull(4).orEmpty(),
    mwst = cols.getOrNull(5).orEmpty(),
    netto = cols.getOrNull(6).orEmpty()
)

@Composable
fun SysBetriebsausgabenScreen() {
    val scope = rememberCoroutineScope()
    var belege by remember { mutableStateOf(emptyList<All>()) }
    var positionen by remember { mutableStateOf(emptyList<All>()) }
    var selectedBelegId by remember { mutableStateOf<String?>(null) }
    var message by remember { mutableStateOf("") }

    suspend fun reload(preferredId: String? = selectedBelegId) {
        belege = EingangsbelegeApi.loadBelege()
        selectedBelegId = preferredId?.takeIf { id -> belege.any { it.id == id } }
        positionen = selectedBelegId?.let { EingangsbelegeApi.loadPositionen(it) }.orEmpty()
    }

    LaunchedEffect(Unit) {
        EingangsbelegeApi.processEingang()
        reload()
    }
    LaunchedEffect(selectedBelegId) {
        positionen = selectedBelegId?.let { EingangsbelegeApi.loadPositionen(it) }.orEmpty()
    }

    val selectedBeleg = belege.firstOrNull { it.id == selectedBelegId }

    fun chooseAndUploadPdf() {
        val beleg = selectedBeleg ?: return
        val input = document.createElement("input") as? HTMLInputElement ?: return
        input.type = "file"
        input.accept = "application/pdf,.pdf"
        input.onchange = {
            val file = input.files?.item(0)
            if (file != null) scope.launch {
                message = if (EingangsbelegeApi.uploadPdf(beleg.id, file)) {
                    reload(beleg.id)
                    "PDF wurde im Eingangsrechnungsordner abgelegt."
                } else "PDF konnte nicht gespeichert werden."
            }
            null
        }
        input.click()
    }

    Div({ style { width(100.percent); height(100.percent); minHeight(0.px); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column) } }) {
        if (message.isNotBlank()) {
            Div({ style { padding(8.px, 14.px); color(Color.white); backgroundColor(Color("#263447")) } }) { Text(message) }
        }
        Div({ style { flex(1); minHeight(0.px) } }) {
            TabularSplitScreen(
                initialRatio = 0.54f,
                minRatio = 0.25f,
                maxRatio = 0.78f,
                screen1 = {
                    TabularScreen(
                        screenKey = "Betriebsausgaben",
                        rollenKey = "Betriebsausgaben",
                        title = "Betriebsausgaben",
                        headers = belegHeaders,
                        data = belege.map { beleg ->
                            beleg.id to listOf(
                                beleg.belegnummer, beleg.belegdatum, beleg.haendler,
                                beleg.brutto, beleg.mwst, beleg.netto,
                                if (beleg.pdf.isNotBlank()) TABULAR_DOCUMENT_ICON_VALUE else ""
                            )
                        },
                        editable = true,
                        includeMetadata = false,
                        nonEditableCols = setOf("Brutto", "MwSt.", "Netto", "PDF"),
                        onRowClick = { selectedBelegId = it },
                        onCellClick = { rowId, colIndex ->
                            val documentBeleg = belege.firstOrNull { it.id == rowId && it.pdf.isNotBlank() }
                            if (colIndex == belegHeaders.lastIndex && documentBeleg != null) {
                                scope.launch {
                                    if (!EingangsbelegeApi.openPdf(rowId, documentBeleg.pdf)) message = "Beleg konnte nicht geladen werden."
                                }
                            }
                        },
                        onSelectedRowsChange = { selected ->
                            selected.singleOrNull()?.let { selectedBelegId = it }
                        },
                        extraNavButtons = listOf(
                            TabularToolbarButton("PDF hochladen", "images/upload.svg", selectedBeleg != null, ::chooseAndUploadPdf)
                        ),
                        createRow = { newId ->
                            if (belege.none { it.id == newId }) {
                                belege = belege + All(id = newId, brutto = "0,00", mwst = "0,00", netto = "0,00")
                            }
                            selectedBelegId = newId
                            listOf("", "", "", "0,00", "0,00", "0,00", "")
                        },
                        onCellChanged = { rowId, column, value ->
                            val current = belege.firstOrNull { it.id == rowId } ?: All(id = rowId)
                            val changed = when (column) {
                                "Belegnummer" -> current.copy(belegnummer = value)
                                "Belegdatum" -> current.copy(belegdatum = value)
                                "Händler" -> current.copy(haendler = value)
                                else -> current
                            }
                            belege = belege.filterNot { it.id == rowId } + changed
                            selectedBelegId = rowId
                        },
                        onCommit = { rows ->
                            scope.launch {
                                var lastId = selectedBelegId
                                rows.forEach { (rowId, cols) ->
                                    val saved = EingangsbelegeApi.saveBeleg(
                                        All(
                                            id = rowId,
                                            belegnummer = cols.getOrNull(0).orEmpty(),
                                            belegdatum = cols.getOrNull(1).orEmpty(),
                                            haendler = cols.getOrNull(2).orEmpty(),
                                            brutto = cols.getOrNull(3).orEmpty(),
                                            mwst = cols.getOrNull(4).orEmpty(),
                                            netto = cols.getOrNull(5).orEmpty(),
                                            pdf = cols.getOrNull(6).orEmpty()
                                        )
                                    )
                                    if (lastId == rowId) lastId = saved?.id ?: rowId
                                }
                                reload(lastId)
                            }
                        },
                        onDeleteRows = { ids ->
                            ids.forEach { EingangsbelegeApi.deleteBeleg(it) }
                            reload(null)
                        },
                        onRefresh = { scope.launch { reload() } }
                    )
                },
                screen2 = selectedBeleg?.let { beleg ->
                    {
                        TabularScreen(
                            screenKey = "BetriebsausgabenPositionen",
                            entityScope = beleg.id,
                            rollenKey = "Betriebsausgaben",
                            title = "Positionen ${beleg.belegnummer.ifBlank { beleg.id }}",
                            headers = positionHeaders,
                            data = positionen.map { position ->
                                position.id to listOf(
                                    position.pos, beleg.belegnummer, position.artikelbezeichnung, position.menge,
                                    position.brutto, position.mwst, position.netto
                                )
                            },
                            editable = true,
                            includeMetadata = false,
                            nonEditableCols = setOf("Belegnummer", "MwSt.", "Netto"),
                            onCellEditTransform = { _, column, value, row ->
                                val next = row.toMutableList()
                                if (column == "Brutto") {
                                    val (mwst, netto) = calculatedMoney(value)
                                    next[4] = value
                                    next[5] = mwst
                                    next[6] = netto
                                }
                                next
                            },
                            onCellChanged = { rowId, column, value ->
                                var index = positionen.indexOfFirst { it.id == rowId }
                                if (index < 0) {
                                    positionen = positionen + All(
                                        id = rowId,
                                        pos = (positionen.size + 1).toString(),
                                        belegId = beleg.id,
                                        belegnummer = beleg.belegnummer,
                                        menge = "1",
                                        brutto = "0,00",
                                        mwst = "0,00",
                                        netto = "0,00",
                                        mwstSatz = "19"
                                    )
                                    index = positionen.lastIndex
                                }
                                if (index >= 0) {
                                    val old = positionen[index]
                                    val next = when (column) {
                                        "Pos." -> old.copy(pos = value)
                                        "Artikelbezeichnung" -> old.copy(artikelbezeichnung = value)
                                        "Menge" -> old.copy(menge = value)
                                        "Brutto" -> {
                                            val (mwst, netto) = calculatedMoney(value)
                                            old.copy(brutto = value, mwst = mwst, netto = netto)
                                        }
                                        else -> old
                                    }
                                    positionen = positionen.toMutableList().also { it[index] = next }
                                    val brutto = positionen.sumOf { parseEuro(it.brutto) }
                                    val mwst = positionen.sumOf { parseEuro(it.mwst) }
                                    val netto = positionen.sumOf { parseEuro(it.netto) }
                                    belege = belege.map { if (it.id == beleg.id) it.copy(brutto = euro(brutto), mwst = euro(mwst), netto = euro(netto)) else it }
                                }
                            },
                            createRow = { newId -> listOf((positionen.size + 1).toString(), beleg.belegnummer, "", "1", "0,00", "0,00", "0,00") },
                            onCommit = { rows ->
                                scope.launch {
                                    val savedBeleg = EingangsbelegeApi.saveBeleg(beleg)
                                    if (savedBeleg == null) {
                                        message = "Belegnummer, Belegdatum und Händler bitte zuerst vollständig eintragen."
                                    } else {
                                        EingangsbelegeApi.savePositionen(savedBeleg.id, rows.map { (id, cols) -> positionFromRow(id, cols, savedBeleg) })
                                        reload(savedBeleg.id)
                                    }
                                }
                            },
                            onDeleteRows = { ids ->
                                val remaining = positionen.filterNot { it.id in ids }
                                EingangsbelegeApi.savePositionen(beleg.id, remaining)
                                reload(beleg.id)
                            },
                            onRefresh = { scope.launch { reload(beleg.id) } }
                        )
                    }
                }
            )
        }
    }
}
